import type { Client } from '@bugsnag/js'

import { userStore } from '$data/stores'
import {
  errorReportingSettings,
  RedactedKeys,
  ReportingStages,
  scrubDeep,
  scrubString
} from '$lib/errorReportingConfig'

/*
  Browser error reporting to Bugsnag. Started from src/hooks.client.ts.

  OFF WITHOUT A KEY: with PUBLIC_BUGSNAG_API_KEY unset at build time,
  __BUGSNAG_API_KEY__ is the constant '' and the guard below is dead code to
  Rollup, so `import('@bugsnag/js')` is dropped and the bundle ships no Bugsnag
  chunk. Nothing loads and nothing is sent.

  PRIVACY: Bugsnag only ever gets the user and account UUIDs. Emails, tokens
  and keys are scrubbed from messages, URLs and breadcrumbs, and the `user`
  (click text) and `log` (console arguments) breadcrumb types are disabled
  because either can carry an email address shown on the page.
*/

let client: Promise<Client | null> = Promise.resolve(null)

export const startErrorReporting = () => {
  if (!__BUGSNAG_API_KEY__) return

  const settings = errorReportingSettings({
    apiKey: __BUGSNAG_API_KEY__,
    appVersion: __APP_VERSION__,
    releaseStage: __RELEASE_STAGE__
  })
  if (!settings) return

  // Not awaited: the app must not wait on, or fail with, the reporter.
  client = import('@bugsnag/js')
    .then(({ default: Bugsnag }) => {
      const bugsnag = Bugsnag.start({
        ...settings,
        enabledReleaseStages: ReportingStages,
        // window.onerror and unhandledrejection. These are Bugsnag's defaults;
        // they are spelled out because the task depends on them.
        enabledErrorTypes: { unhandledExceptions: true, unhandledRejections: true },
        enabledBreadcrumbTypes: ['error', 'manual', 'navigation', 'request', 'state'],
        redactedKeys: RedactedKeys,
        collectUserIp: false,
        generateAnonymousId: false,
        onBreadcrumb: (breadcrumb) => {
          breadcrumb.message = scrubString(breadcrumb.message)
          breadcrumb.metadata = scrubDeep(breadcrumb.metadata)
        },
        onError: (event) => {
          for (const error of event.errors) error.errorMessage = scrubString(error.errorMessage)
          if (event.request.url) event.request.url = scrubString(event.request.url)
          if (event.request.referer) event.request.referer = scrubString(event.request.referer)
          if (event.context) event.context = scrubString(event.context)
        }
      })

      userStore.subscribe((user: { uuid?: string; account?: { uuid?: string } } | null) => {
        bugsnag.setUser(user?.uuid ?? null)
        if (user?.account?.uuid) bugsnag.addMetadata('account', { id: user.account.uuid })
        else bugsnag.clearMetadata('account')
      })

      return bugsnag
    })
    .catch((e) => {
      console.error('Bugsnag failed to start', e)
      return null
    })
}

/** Report an error SvelteKit caught (load/render), which never reaches window.onerror. */
export const reportError = (error: unknown, metadata: Record<string, unknown>) => {
  client.then((bugsnag) =>
    bugsnag?.notify(error as Error, (event) => {
      event.addMetadata('sveltekit', scrubDeep(metadata))
    })
  )
}
